import { useCallback, useEffect, useState } from 'react';
import { useDropzone } from 'react-dropzone';
import Button from 'react-bootstrap/Button';
import { TfiUpload } from 'react-icons/tfi';
import styled from 'styled-components';

import { useNewProject } from '@contexts/NewProject.context';
import { PROJECT_MODE } from '@utils/constants';
import { useAuth } from '@contexts/User.context';
import { useToast } from '@contexts/Toast.context';
import { useConfirm } from '@contexts/Confirm.context';
import useDragActive from '@newProject/utils/hooks/useDragActive';
import {
	getTotalFilesSize,
	getFileType,
	convertToMegabytes,
	hasLazWithoutImages,
	getLazWithoutImagesMessage,
} from '@newProject/utils/helpers';

// JSX
import FileItem from './components/FileItem';
import UploadButton from './components/UploadButton';

const UploadFieldWrap = styled.div`
	border: 1px solid rgba(${({ theme }) => theme.colors.borderLightRgb}, 0.2);
	padding: 1rem;
	border-radius: 0.375rem;
`;

const UploadFieldContainer = styled.div`
	display: flex;
	flex-direction: column;
	padding: 2rem 1rem;
	text-align: center;
	transition: background-image 0.2s ease-out;
	--border-size: 3px;
	--border-angle: 0turn;
	border-radius: 0.375rem;
	background-image:
		conic-gradient(from var(--border-angle), #171717, #171717 50%, #171717),
		conic-gradient(from var(--border-angle), transparent 20%, #fff, #198754);
	background-size:
		calc(100% - (var(--border-size) * 2)) calc(100% - (var(--border-size) * 2)),
		cover;
	background-position: center center;
	background-repeat: ${({ $animationActive }) => ($animationActive ? 'no-repeat' : 'initial')};
	background-color: #171717;
	animation: bg-spin 3s linear infinite;
	animation-play-state: ${({ $animationActive }) => ($animationActive ? 'running' : 'paused')};

	@keyframes bg-spin {
		to {
			--border-angle: 1turn;
		}
	}
	@property --border-angle {
		syntax: '<angle>';
		inherits: true;
		initial-value: 0turn;
	}
`;

const FileList = styled.div`
	background-color: #171717;
	border-radius: 0.375rem;
	height: 300px;
	max-height: 300px;
	overflow-y: auto;
`;

const UploadIcon = styled(TfiUpload)`
	margin-inline: auto;
	margin-block: 0.5rem;
	font-size: 1.5rem;
`;

const FileListHeader = styled.div`
	display: flex;
	justify-content: space-between;
	align-items: end;

	margin-top: 1rem;
	margin-bottom: 0.5rem;

	& > p {
		margin: 0;
	}

	& > button {
		padding-block: 0.125rem;
		padding-inline: 0.35rem;
		text-decoration: none;

		color: rgba(225, 225, 225, 0.5);
		border: 1px solid rgba(225, 225, 225, 0.5);
		&:hover {
			background: rgba(225, 225, 225, 0.2);
			color: rgba(225, 225, 225, 1);
			border: 1px solid rgba(225, 225, 225, 1);
		}

		&:focus-visible {
			box-shadow: 0 0 0 4px rgba(225, 225, 225, 0.2);
			color: rgba(225, 225, 225, 0.5);
			border: 1px solid rgba(225, 225, 225, 0.5);
		}

		&:active {
			background: rgba(225, 225, 225, 0.1) !important;
			color: rgba(225, 225, 225, 0.75) !important;
			border: 1px solid rgba(225, 225, 225, 0.75) !important;
		}
	}
`;

const NoFilesText = styled.small`
	display: grid;
	place-items: center;
	color: #b0b0b0;
	height: 100%;
	width: 100%;
	margin: 0;
`;

const UploadField = ({ ...props }) => {
	const { addToast } = useToast();
	const { confirm } = useConfirm();
	const { mode, files, setFiles, monthlyProjectsLimit, isSubmitting } = useNewProject();

	const { imageLimit } = monthlyProjectsLimit;
	const { ORTHOPHOTO, SINGLE_IMAGE } = PROJECT_MODE;
	const { tierTrial: isTrial, roleAdmin: isAdmin } = useAuth();

	const getMaxFiles = () => {
		if (isAdmin) return 0;
		if (isTrial) return 100;

		switch (mode) {
			case ORTHOPHOTO:
				return imageLimit ?? 0;

			case SINGLE_IMAGE:
				return 1000;

			default:
				return 0;
		}
	};

	const getLimitText = maxFiles => {
		if (isAdmin || !maxFiles) return '';
		if (isTrial) return `Limit of ${maxFiles} images or 200MB orthophoto on trial account.`;

		return `Limit of ${maxFiles} images for your subscription type.`;
	};

	const getFileTypeInfo = () => {
		if (mode === ORTHOPHOTO) return '(.jpg, .tif, .laz)';
		if (mode === SINGLE_IMAGE) return '(.jpg, .laz)';

		return '';
	};

	const getAcceptableFileTypes = () => {
		if (mode === ORTHOPHOTO)
			return {
				'image/jpeg': ['.jpg', '.jpeg'],
				'image/tiff': ['.tif', '.tiff'],
				'application/octet-stream': ['.laz'],
			};

		if (mode === SINGLE_IMAGE)
			return {
				'image/jpeg': ['.jpg', '.jpeg'],
				'application/octet-stream': ['.laz'],
			};

		return {};
	};

	const maxFiles = getMaxFiles();
	const limitText = getLimitText(maxFiles);
	const fileTypeInfo = getFileTypeInfo();
	const acceptableFileTypes = getAcceptableFileTypes();

	const [updateFileHandles, setUpdateFileHandles] = useState(0);
	useEffect(() => {
		// Update file handles to ensure user can upload same file after removing it.
		setUpdateFileHandles(prev => prev + 1);

		// Clear files if switching from orthophoto to single image with a tiff file (any position, e.g. after a .laz)
		if (mode == SINGLE_IMAGE && files.some(file => getFileType(file) === 'tif')) setFiles([]);

		// Limit files to maxFiles
		if (files.length > maxFiles && maxFiles != 0) {
			setFiles(files.slice(0, maxFiles));
			addToast({
				id: 'file-limit-exceeded',
				title: 'File limit exceeded',
				message: `The maximum number of files is ${maxFiles}. Excess files were rejected.`,
				bg: 'warning',
			});
		}
	}, [files, mode]);

	// Handles rejected files and displays an error message
	const handleFileRejection = useCallback(
		fileRejection => {
			const errorCode = fileRejection?.[0]?.errors?.[0]?.code;
			const errorMessages = {
				'too-many-files': `Too many files. Maximum number of files is ${maxFiles}.`,
				'file-invalid-type': `Invalid file type. ${mode == ORTHOPHOTO ? 'Only .jpg/jpeg, .tif/tiff and .laz' : 'Only .jpg/jpeg and .laz'} files are allowed.`,
			};

			addToast({
				id: `file-upload-error-${errorCode}`,
				title: 'File selection error',
				message: errorMessages[errorCode] ?? 'Unknown error uploading files.',
				bg: 'danger',
			});
		},
		[mode, maxFiles]
	);

	// Handles accepted files and dispatches them to context
	const handleAcceptedFiles = useCallback(
		acceptedFiles => {
			const sanitizedFiles = [];
			while (acceptedFiles.length) {
				const file = acceptedFiles.shift();

				// Removes anything not alphanumeric or . _ - from file name and replaces spaces with _
				const sanitizedName = file.name.replaceAll(' ', '_').replace(/[^a-z0-9._-]/gi, '');
				sanitizedFiles.push(new File([file], sanitizedName, { type: file.type }));
			}

			const allFiles = [...files, ...sanitizedFiles];
			let firstValidProjectFileType;
			const sortedFiles = {};
			const invalidFileTypes = [];

			// Sort files by type and filter invalid files
			for (const file of allFiles) {
				const type = getFileType(file);
				if (!['jpg', 'tif', 'laz'].includes(type)) {
					invalidFileTypes.push(`.${type}`);
					continue;
				}

				if (!firstValidProjectFileType && ['jpg', 'tif'].includes(type))
					firstValidProjectFileType = type;

				if (!sortedFiles[type]) sortedFiles[type] = [file];
				else sortedFiles[type].push(file);
			}

			// Returns handler for project file type rules
			const projectFileTypeRules = {
				tif: () => {
					if (sortedFiles?.tif?.length > 1) {
						sortedFiles.tif.length = 1;
						addToast({
							id: 'tif-file-limit-exceeded',
							title: 'TIF File limit exceeded',
							message:
								'You can only upload one .tif/tiff file per project. Excess files were rejected.',
							bg: 'warning',
						});
					}
					if (sortedFiles?.jpg?.length) {
						sortedFiles.jpg = [];
						addToast({
							id: 'jpg-tif-mix-error',
							title: 'File type mix error',
							message:
								'You can not mix .tif/tiff and .jpg/jpeg files. The .jpg/jpeg files were rejected.',
							bg: 'warning',
						});
					}
				},
				jpg: () => {
					if (sortedFiles.jpg.length > maxFiles && maxFiles != 0) {
						sortedFiles.jpg.length = maxFiles;
						addToast({
							id: 'jpg-file-limit-exceeded',
							title: 'JPG File limit exceeded',
							message: `You can only upload up t	o ${maxFiles} images. Excess files were rejected.`,
							bg: 'warning',
						});
					}

					if (sortedFiles?.tif?.length) {
						sortedFiles.tif = [];
						addToast({
							id: 'jpg-tif-mix-error',
							title: 'File type mix error',
							message:
								'You can not mix .tif/tiff and .jpg/jpeg files. The .tif/tiff files were rejected.',
							bg: 'warning',
						});
					}
				},
			};

			// Apply rules
			const ruleHandler = projectFileTypeRules[firstValidProjectFileType];
			if (ruleHandler) ruleHandler();

			if (sortedFiles?.laz?.length > 1) {
				sortedFiles.laz.length = 1;
				addToast({
					id: 'laz-file-limit-exceeded',
					title: 'LAZ File limit exceeded',
					message:
						'You can only upload one .laz file per project. Excess files were rejected.',
					bg: 'warning',
				});
			}

			// Flatten sorted files to single depth array again
			const validatedFiles = Object.values(sortedFiles).flat(Infinity);

			// Get total size of filtered files
			const totalFilteredFilesSize = getTotalFilesSize(validatedFiles);

			// Deny trial users from uploading files larger than 200MB
			if (isTrial && convertToMegabytes(totalFilteredFilesSize) > 200)
				return addToast({
					id: 'trial-size-limit-exceeded',
					title: 'Trial size limit exceeded',
					message: 'You have reached the 200MB limit for trial accounts.',
					bg: 'warning',
				});

			if (invalidFileTypes.length) {
				let typeString = '';
				if (invalidFileTypes.length < 2) typeString = invalidFileTypes[0];
				else {
					const last = invalidFileTypes.pop();
					typeString = `${invalidFileTypes.join(', ')} and ${last}`;
				}

				addToast({
					id: 'invalid-file-types',
					title: 'Unsupported file types',
					message: `The following file types are not supported and were rejected: ${typeString}.`,
					bg: 'warning',
				});
			}

			// Warn when only a .laz file is present, since it needs image files to create a project
			if (hasLazWithoutImages(validatedFiles, mode))
				addToast({
					id: 'laz-without-images',
					title: 'Image files required',
					message: getLazWithoutImagesMessage(mode),
					bg: 'warning',
				});

			// Set files to context
			setFiles(validatedFiles);
		},
		[files, mode]
	);

	// Dropzone hook
	const dragActive = useDragActive(['Files']);
	const { getRootProps, getInputProps } = useDropzone({
		onDropAccepted: handleAcceptedFiles,
		onDropRejected: handleFileRejection,
		maxFiles: maxFiles, // Only limits max files uploaded at once
		accept: acceptableFileTypes,
		disabled: isSubmitting,
		noClick: true,
		preventDropOnDocument: false,
		noKeyboard: true,
	});

	const handleRemoveAllFiles = async () => {
		const userConfirmed = await confirm({
			title: 'Remove All Files',
			message: 'Are you sure you want to remove all files?',
		});

		if (userConfirmed) setFiles([]);
	};

	return (
		<UploadFieldWrap {...props}>
			<p className="mb-1">Project files {fileTypeInfo}</p>
			<UploadFieldContainer
				key={updateFileHandles}
				{...getRootProps()}
				$animationActive={dragActive}>
				<input {...getInputProps()} />
				<UploadIcon />
				<p>
					{dragActive ? 'Drop the files here' : 'Drag and drop some files here.'}
					<br />
					<small className="text-muted">{limitText}</small>
				</p>

				<UploadButton inputProps={getInputProps()} />
			</UploadFieldContainer>

			<FileListHeader>
				<p>Files:</p>

				{files.length > 1 && (
					<Button variant="link" onClick={handleRemoveAllFiles}>
						Remove All
					</Button>
				)}
			</FileListHeader>
			<FileList>
				{files.length ? (
					files.map((file, index) => (
						<FileItem key={`${file.name}-${index}`} file={file} index={index} />
					))
				) : (
					<NoFilesText>No files uploaded</NoFilesText>
				)}
			</FileList>
		</UploadFieldWrap>
	);
};

export default UploadField;
